<template>
  <div>
    <!-- Backdrop Overlay -->
    <Transition name="fade">
      <div
        v-if="visible"
        class="fixed inset-0 bg-black bg-opacity-50 z-[2000]"
        @click="closeDrawer"
      ></div>
    </Transition>

    <!-- Drawer Panel -->
    <Transition name="slide">
      <div
        v-if="visible"
        class="fixed top-0 right-0 h-full w-96 bg-surface-0 dark:bg-surface-900 shadow-2xl z-[2001] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700"
        >
          <h2
            class="text-xl font-semibold text-surface-900 dark:text-surface-0"
          >
            {{ t("principal.drawer.menu_title") }}
          </h2>
          <button
            @click="closeDrawer"
            class="p-2 rounded-full hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            :title="t('principal.drawer.close')"
          >
            <i class="pi pi-times text-surface-900 dark:text-surface-0"></i>
          </button>
        </div>

        <!-- Menu Items -->
        <div class="flex-1 overflow-y-auto p-4">
          <nav class="space-y-2">
            <!-- Switch Theme Button -->
            <button
              @click="handleThemeSwitch"
              class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors text-left"
            >
              <i
                :class="isDarkTheme ? 'pi pi-moon' : 'pi pi-sun'"
                class="text-surface-900 dark:text-surface-0"
              ></i>
              <span class="text-surface-900 dark:text-surface-0">{{
                t("principal.drawer.switch_theme")
              }}</span>
            </button>

            <!-- Switch Language Button -->
            <button
              @click="handleLanguageSwitch"
              class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors text-left"
            >
              <i class="pi pi-globe text-surface-900 dark:text-surface-0"></i>
              <span class="text-surface-900 dark:text-surface-0">{{
                t("principal.drawer.switch_language")
              }}</span>
              <span
                class="ml-auto text-sm text-surface-500 dark:text-surface-400 uppercase"
              >
                {{ currentLocale }}
              </span>
            </button>

            <!-- Company Management Button (Owner/Admin only) -->
            <button
              v-if="showCompanyManagement"
              @click="handleCompanyManagement"
              class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors text-left"
            >
              <i
                class="pi pi-briefcase text-surface-900 dark:text-surface-0"
              ></i>
              <span class="text-surface-900 dark:text-surface-0">{{
                t("principal.drawer.company_management")
              }}</span>
            </button>

            <!-- Admin Panel Button (Admin only) -->
            <button
              v-if="showAdminPanel"
              @click="handleAdminPanel"
              class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors text-left"
            >
              <i class="pi pi-shield text-surface-900 dark:text-surface-0"></i>
              <span class="text-surface-900 dark:text-surface-0">{{
                t("principal.drawer.admin_panel")
              }}</span>
            </button>

            <!-- Separator -->
            <div
              class="border-t border-surface-200 dark:border-surface-700 my-2"
            ></div>

            <!-- Logout Button -->
            <button
              @click="handleLogout"
              class="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left text-red-600 dark:text-red-400"
            >
              <i class="pi pi-sign-out"></i>
              <span>{{ t("principal.drawer.logout") }}</span>
            </button>
          </nav>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { useLayout } from "@/layout/composables/layout";
import { useAuthStore } from "@/stores/auth";
import { CustomerRole } from "@/types/customerRoles";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(["update:visible"]);

// Composables
const { t, locale } = useI18n({ useScope: "global" });
const router = useRouter();
const authStore = useAuthStore();
const { isDarkTheme, toggleDarkMode } = useLayout();

/**
 * Close the drawer by emitting the update:visible event
 */
const closeDrawer = () => {
  console.log("[AppDrawer] Closing drawer");
  emit("update:visible", false);
};

/**
 * Handle theme switch action
 */
const handleThemeSwitch = () => {
  console.log("[AppDrawer] Theme switch clicked");
  toggleDarkMode();
  // Note: We don't close the drawer on theme switch to allow users to see the change
};

/**
 * Computed property to get the current locale
 */
const currentLocale = computed(() => locale.value);

/**
 * Handle language switch action
 */
const handleLanguageSwitch = () => {
  const newLocale = locale.value === "it" ? "en" : "it";
  console.log(
    `[AppDrawer] Language switch clicked, changing from ${locale.value} to ${newLocale}`,
  );
  locale.value = newLocale;
  // Save locale to localStorage for persistence
  localStorage.setItem("locale", newLocale);
  // Note: We don't close the drawer on language switch to allow users to see the change
};

/**
 * Handle company management navigation
 */
const handleCompanyManagement = () => {
  console.log("[AppDrawer] Company management clicked");
  router.push({
    name:
      authStore.isAdmin && !authStore.hasCompany()
        ? "admin-companies"
        : "company",
  });
  closeDrawer();
};

/**
 * Handle admin panel navigation
 */
const handleAdminPanel = () => {
  console.log("[AppDrawer] Admin panel clicked");
  router.push({ name: "admin-root" });
  closeDrawer();
};

/**
 * Handle logout action
 */
const handleLogout = async () => {
  console.log("[AppDrawer] Logout clicked");
  closeDrawer();
  await authStore.logout();
  router.push({ name: "login" });
};

/**
 * Computed property to determine if company management should be shown
 * Visible to OWNER or SERVICE role, or ADMIN users
 */
const showCompanyManagement = computed(() => {
  // Check if user is admin
  if (authStore.isAdmin) {
    console.log("[AppDrawer] Showing company management (user is admin)");
    return true;
  }

  // Check if user has company and is owner or service
  if (authStore.hasCompany()) {
    const userRole = authStore.userRole;
    const isOwnerOrAbove =
      userRole === CustomerRole.OWNER || userRole === CustomerRole.SERVICE;

    if (isOwnerOrAbove) {
      console.log(
        `[AppDrawer] Showing company management (user role: ${userRole})`,
      );
    } else {
      console.log(
        `[AppDrawer] Hiding company management (user role: ${userRole})`,
      );
    }

    return isOwnerOrAbove;
  }

  console.log("[AppDrawer] Hiding company management (no company)");
  return false;
});

/**
 * Computed property to determine if admin panel should be shown
 * Visible only to ADMIN users
 */
const showAdminPanel = computed(() => {
  const isAdmin = authStore.isAdmin;
  if (isAdmin) {
    console.log("[AppDrawer] Showing admin panel (user is admin)");
  } else {
    console.log("[AppDrawer] Hiding admin panel (user is not admin)");
  }
  return isAdmin;
});

// Log drawer visibility changes
onMounted(() => {
  console.log("[AppDrawer] Component mounted");
});
</script>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for drawer panel */
.slide-enter-active,
.slide-leave-active {
  transition: transform 300ms ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
